import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RefreshCw, Tags, ChevronDown } from "lucide-react";
import { PRESETS, presetRange } from "./roasUtil";

function CampaignPicker({ campaigns, picked, setPicked }) {
  const [q, setQ] = useState("");
  const rows = campaigns.filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="btn text-xs" data-testid="roas-campaign-picker">{picked.length ? `${picked.length} campaign${picked.length > 1 ? "s" : ""}` : "All campaigns"}<ChevronDown size={12} /></button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-2" align="start">
        <input className="w-full px-2.5 py-1.5 rounded border text-sm mb-2" placeholder="Search campaigns" value={q} onChange={(e) => setQ(e.target.value)} data-testid="roas-campaign-search" />
        <div className="flex gap-3 text-xs mb-2">
          <button className="text-[var(--ai)]" onClick={() => setPicked([...new Set([...picked, ...rows.map((c) => c.campaign_id)])])} data-testid="roas-campaign-select-shown">Select shown</button>
          <button className="text-[var(--text-dim)]" onClick={() => setPicked([])} data-testid="roas-campaign-clear">Clear (= all)</button>
        </div>
        <div className="max-h-72 overflow-auto space-y-0.5">
          {rows.map((c) => (
            <label key={c.campaign_id} className="flex items-start gap-2 text-xs py-1 px-1 rounded hover:bg-[var(--panel-2)] cursor-pointer">
              <input type="checkbox" checked={picked.includes(c.campaign_id)} onChange={() => toggle(c.campaign_id)} data-testid={`roas-pick-${c.campaign_id}`} />
              <span className="flex-1">{c.name}<span className="block text-[10px] text-[var(--text-faint)]">{c.account_name} · {c.status?.toLowerCase()}</span></span>
            </label>
          ))}
          {!rows.length && <div className="text-xs text-[var(--text-dim)] p-2">No campaigns in this group.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const POOLS = { ours: ["ours"], ours_test: ["ours", "test"], test: ["test"], client: ["client"], unclassified: [""] };

export function RoasControls({ f, setF, campaigns, onSync, syncing, onClassify, unclassified }) {
  const pool = campaigns.filter((c) => f.owner === "all" || (POOLS[f.owner] || []).includes(c.owner || ""));
  const set = (p) => setF({ ...f, ...p });
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="roas-controls">
      <div className="flex flex-wrap gap-1">
        {PRESETS.map(([k, l]) => (
          <button key={k} className={`btn text-xs ${f.preset === k ? "btn-primary" : ""}`} onClick={() => { const [s, u] = presetRange(k); set({ preset: k, since: s, until: u }); }} data-testid={`roas-preset-${k}`}>{l}</button>
        ))}
      </div>
      <input type="date" className="px-2 py-1 rounded border text-xs" value={f.since} onChange={(e) => set({ preset: "", since: e.target.value })} data-testid="roas-since" />
      <span className="text-xs text-[var(--text-dim)]">to</span>
      <input type="date" className="px-2 py-1 rounded border text-xs" value={f.until} onChange={(e) => set({ preset: "", until: e.target.value })} data-testid="roas-until" />
      <select className="px-2 py-1 rounded border text-xs bg-transparent" value={f.owner} onChange={(e) => set({ owner: e.target.value, picked: [] })} data-testid="roas-owner">
        <option value="ours">Our campaigns</option><option value="ours_test">Ours + test</option><option value="test">Test campaigns</option><option value="client">Client campaigns</option>
        <option value="unclassified">Unclassified</option><option value="all">All campaigns</option>
      </select>
      <CampaignPicker campaigns={pool} picked={f.picked} setPicked={(picked) => set({ picked })} />
      <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={f.compare} onChange={(e) => set({ compare: e.target.checked })} data-testid="roas-compare" />Compare to previous period</label>
      <div className="ml-auto flex gap-2">
        <button className="btn text-xs" onClick={onClassify} data-testid="roas-classify-open"><Tags size={12} />Classify campaigns{unclassified ? <span className="ml-1 px-1.5 rounded bg-[var(--warn)] text-black">{unclassified}</span> : null}</button>
        <button className="btn text-xs" onClick={onSync} disabled={syncing} data-testid="roas-sync"><RefreshCw size={12} className={syncing ? "animate-spin" : ""} />{syncing ? "Syncing\u2026" : "Sync Meta"}</button>
      </div>
    </div>
  );
}
